"use client";

import { Fragment, useEffect, useState } from "react";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import Image from "next/image";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { PageCompetitionAffiliateOldDesignSchema } from "@/sanity/pages/PageCompetitionAffiliateOldDesignTemplate";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { resolveLink } from "@/sanity/globals/CTA";

import bg from "@/public/global/bg-art.jpg";
import logo from "@/public/logo-black.svg";
import arrow from "@/public/global/dotted-arrow.svg";
import Countdown from "@/components/pageComponents/comp-affiliate-old-design/Countdown";
import ChipSerif from "@/components/global/ui/ChipSerif";

interface HeroProps {
  data: PageCompetitionAffiliateOldDesignSchema;
  className?: string;
  showTopBanner?: boolean;
}

export default function Hero({ data, className, showTopBanner }: HeroProps) {
  const homeHref = resolveLink("/", data?.settings?.language || DEFAULT_LANGUAGE);
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!homeHref) return;
    setFinalLink(homeHref);
  }, [homeHref]);

  return (
    <section
      id="hero-section"
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[5rem] pb-[3.25rem] lg:pb-[4.5rem]">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.1]"
            src={bg}
            quality={100}
            alt=""
          />
        </div>

        <div className="container flex relative z-10 flex-col items-center px-[1.5rem]">
          <div className="relative mx-auto w-full lg:px-0 lg:pt-0 max-w-[103rem] z-[1] pt-[2rem] mb-[3.25rem] lg:mb-[5.75rem]">
            <motion.div
              className="flex items-center w-[min(92.875rem,100%)] mx-auto mb-[2.85rem]"
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{
                duration: 0.3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
            >
              <div className="w-[min(91.5rem,100%)] flex justify-between items-center lg:flex-row flex-col">
                <div className="flex gap-[2rem] items-center">
                  <Link href={finalLink}>
                    <Image src={logo} alt="" className="w-[16.25rem]" />
                  </Link>

                  {data?.partnerName && (
                    <Fragment>
                      <Image src={arrow} alt="" className="flex-shrink-0" />
                      <div className="flex items-center gap-[1rem]">
                        <p className="text-[#103037] text-[2rem] lg:text-[2.25rem] font-[500] leading-[116%] mb-0">
                          {data.partnerName}
                        </p>
                      </div>
                    </Fragment>
                  )}
                </div>

                {data?.dates && (
                  <div className="lg:mt-0 mt-[2rem]">
                    <p className="mb-0 font-geist text-black/50  text-[1.75rem] font-[500] leading-[100%]">
                      {data.dates.start} /{" "}
                      <em className="not-italic text-black/90">
                        {data.dates.end}
                      </em>
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            <div className="w-[min(123.75rem,100%)] mx-auto relative">
              <div
                style={{ background: "#103037" }}
                className="absolute left-1/2 -translate-x-1/2 rounded-[2.25rem] top-1/2 -translate-y-1/2 z-[0] !h-[60%] !w-[110rem] lg:block hidden"
              />
              <div
                style={{ background: "rgba(72, 146, 104, 0.55)" }}
                className="absolute left-1/2 -translate-x-1/2 backdrop-blur-[3rem] rounded-[2.25rem] top-1/2 -translate-y-1/2 z-[1] !h-[80%] !w-[102rem] lg:block hidden"
              />

              <motion.div
                className="relative z-[2]"
                initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.3,
                  delay: 0.1,
                  ease: [0.25, 0.4, 0.25, 1],
                }}
              >
                {data?.banner && (
                  <div className="w-[min(94.25rem,100%)] overflow-clip mx-auto lg:p-[0.75rem] rounded-[1.5rem] lg:rounded-[4.45rem] border border-[#DEDEDE]/20 bg-[#103037]">
                    <Image
                      src={data.banner}
                      alt=""
                      width={0}
                      height={0}
                      sizes="100vw"
                      className="w-full h-auto rounded-[inherit]"
                    />
                  </div>
                )}
              </motion.div>
            </div>
          </div>

          {data?.heroChip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={data.heroChip.text} />
            </motion.div>
          )}

          {data?.heroText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="richtext w-[min(92rem,100%)] prose-p:w-[min(64.75rem,90%)] prose-headings:text-[4.5rem] lg:prose-headings:text-[7rem] flex flex-col gap-[1.5rem]"
            >
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                duration: 0.3,
                delay: 0.5,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="w-full lg:w-fit"
            >
              <CTAButton
                className="mt-[3rem] lg:min-w-[60.125rem]"
                data={data.heroCTA}
              />
            </motion.div>
          )}

          {data?.toggleCountdown && (
            <div className="mt-[2.5rem] w-[min(60.125rem,100%)] mx-auto rounded-[1.5rem] lg:rounded-[2.25rem] backdrop-blur-[5.375rem] bg-[#1030378C]">
              <Countdown
                dateTimeString={data.countdown ?? ""}
                lang={(data.countdownTextLang ?? "de") as "de" | "en" | "br"}
              />
            </div>
          )}

          <div className="w-[min(138.375rem,100%)] mx-auto flex justify-center items-center lg:mt-[8rem] lg:flex-row flex-col mt-[4rem]">
            {data?.heroItems?.map((item, index) => (
              <Fragment key={index}>
                <motion.div
                  className="flex items-center gap-[1.5rem] py-[0.5rem] pl-[.5rem] pr-[2rem]"
                  initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.3,
                    delay: 0.3 + index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                >
                  {item.icon && <Image src={item.icon} alt="" />}
                  <p className="mb-0 text-[#103037] font-[500] leading-[140%] text-[2rem]">
                    {item.text}
                  </p>
                </motion.div>

                {index < data.heroItems.length - 1 && (
                  <div className="w-full max-w-[95%] bg-black/10 mx-[1rem] h-[.125rem] lg:h-[4rem] lg:w-[.125rem] my-[1rem] lg:my-0" />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
